import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, X, Music } from 'lucide-react';
import { audioManager } from '../lib/audioManager';

export function NowPlayingBar() {
  const [state, setState] = useState(null);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const unsub = audioManager.subscribe((s) => setState(s));
    // Grab initial state
    setState(audioManager.getNowPlaying());
    return unsub;
  }, []);

  // Progress ticker
  const tick = useCallback(() => {
    if (state && state.audio && !state.audio.paused) {
      setProgress(state.audio.currentTime || 0);
      setDuration(state.audio.duration || 0);
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [state]);

  useEffect(() => {
    if (state && state.audio) {
      rafRef.current = requestAnimationFrame(tick);
      // Also listen for play/pause events to re-render
      const handler = () => setState(audioManager.getNowPlaying());
      state.audio.addEventListener('play', handler);
      state.audio.addEventListener('pause', handler);
      return () => {
        cancelAnimationFrame(rafRef.current);
        state.audio.removeEventListener('play', handler);
        state.audio.removeEventListener('pause', handler);
      };
    } else {
      setProgress(0);
      setDuration(0);
    }
  }, [state, tick]);

  if (!state) return null;

  const pct = duration > 0 ? (progress / duration) * 100 : 0;
  const formatTime = (s) => {
    if (!s || !isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up"
      data-testid="now-playing-bar"
    >
      {/* Progress rail */}
      <div className="h-0.5 bg-gray-800 w-full">
        <div
          className="h-full bg-gradient-to-r from-teal-400 to-gold-400 transition-[width] duration-200"
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="bg-gray-900/95 backdrop-blur-xl border-t border-gray-800/60 px-4 py-2.5">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          {/* Icon */}
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-600 to-teal-800 flex items-center justify-center flex-shrink-0 shadow-lg shadow-teal-900/40">
            <Music size={16} className="text-teal-100" />
          </div>

          {/* Track info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-teal-50 truncate leading-tight" data-testid="now-playing-title">
              {state.title}
            </p>
            {state.subtitle && (
              <p className="text-xs text-teal-400/80 truncate leading-tight">
                {state.subtitle}
              </p>
            )}
          </div>

          {/* Time */}
          {state.audio && duration > 0 && (
            <span className="text-xs text-gray-500 tabular-nums hidden sm:block">
              {formatTime(progress)} / {formatTime(duration)}
            </span>
          )}

          {/* Play/Pause */}
          {state.audio && (
            <button
              onClick={() => audioManager.togglePlayPause()}
              className="w-8 h-8 rounded-full bg-gold-500 hover:bg-gold-400 text-black flex items-center justify-center transition-all hover:scale-110"
              data-testid="now-playing-toggle"
              aria-label={state.isPlaying ? 'Pause' : 'Play'}
            >
              {state.isPlaying
                ? <Pause size={14} fill="currentColor" />
                : <Play size={14} fill="currentColor" className="ml-0.5" />
              }
            </button>
          )}

          {/* Close */}
          <button
            onClick={() => audioManager.stopFromBar()}
            className="w-7 h-7 rounded-full bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white flex items-center justify-center transition-all"
            data-testid="now-playing-close"
            aria-label="Stop"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
